import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import graphQLSegmentsApiService from "../services/GraphQLSegmentsApiService";
import { useSegments } from "../hooks/useSegments";
import { SegmentsSearchInterface } from "./segmentSearchInterface";
import { SegmentsFiltersSidebar } from "./segmentsFilterSidebar";
import { SegmentsResultsList } from "./segmentResultList";
import { UnifiedSetsPanel } from "./UnifiedSetsPanel";
import { ENVIRONMENTS, ENV_STORAGE_KEY } from "../config/environments";
import { switchEnvironment } from "../services/environmentSwitcher";
import { getRecommendedThreshold, resolveQueryThreshold } from "../utils";

// Fallback tier for a bare query typed straight into the search box.
const SEGMENT_DEFAULT_THRESHOLD = getRecommendedThreshold("SEGMENTS");

export const GraphQLTestPage = () => {
  const navigate = useNavigate();

  const savedProxy = (() => { try { return localStorage.getItem(ENV_STORAGE_KEY); } catch (_) { return null; } })();
  const [activeEnv, setActiveEnv] = useState(
    ENVIRONMENTS.find((e) => e.proxy === savedProxy)?.key ?? "dev-int"
  );
  const handleEnvChange = (env) => {
    setActiveEnv(env.key);
    switchEnvironment(env.proxy);
  };
  const [showFilters, setShowFilters] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [activeSubTab, setActiveSubTab] = useState("OA");
  const [activeResultsTab, setActiveResultsTab] = useState("segments");

  // Unified set config
  const [topNSegments, setTopNSegments] = useState(15);
  const [reachGoalM, setReachGoalM] = useState(50);

  // Advanced unified set options
  const [minSegments, setMinSegments] = useState(null);
  const [andMultiplier, setAndMultiplier] = useState(null);
  const [highestScaleRelevanceWeight, setHighestScaleRelevanceWeight] = useState(0.7);
  const [bestPerformanceRelevanceWeight, setBestPerformanceRelevanceWeight] = useState(0.7);
  const [oxsWeight, setOxsWeight] = useState(0.6);
  const [highestScaleStartSize, setHighestScaleStartSize] = useState(null);
  const [bestPerformanceStartSize, setBestPerformanceStartSize] = useState(null);
  const [mostRelevantStartSize, setMostRelevantStartSize] = useState(null);
  const [llmRelevanceGateEnabled, setLlmRelevanceGateEnabled] = useState(true);
  const [segmentRetentionRate, setSegmentRetentionRate] = useState(null);
  const [maxOptimisationRounds, setMaxOptimisationRounds] = useState(null);

  // Direct Segment Search results (separate from Unified Set path)
  const [isSegmentMode, setIsSegmentMode] = useState(false);
  const [segmentSearchResult, setSegmentSearchResult] = useState(null);
  const [segmentSearchLoading, setSegmentSearchLoading] = useState(false);
  const [segmentSearchError, setSegmentSearchError] = useState(null);

  const [unifiedSetType, setUnifiedSetType] = useState("TOP_N");

  // Unified set result — one type at a time (user-selectable)
  const [unifiedResult, setUnifiedResult] = useState(null);
  const [unifiedLoading, setUnifiedLoading] = useState(false);
  const [unifiedError, setUnifiedError] = useState(null);

  // NLP / LLM gate state
  const [nlpMode, setNlpMode] = useState(true);
  const [nlpText, setNlpText] = useState("");
  const [nlpLoading, setNlpLoading] = useState(false);
  const [nlpError, setNlpError] = useState(null);
  const [nlpInterpretation, setNlpInterpretation] = useState(null);

  const unifiedAbortRef = useRef(null);
  const segmentSearchAbortRef = useRef(null);
  const lastSearchTimestamp = useRef(null);
  const lastSearchContextRef = useRef(null);

  const {
    filters,
    updateFilter,
    toggleArrayFilter,
    clearAllFilters,
    expandedSections,
    toggleSection,
    filterOptions,
    isLoadingFilterOptions,
    hasFilterError,
    filterError,
    retryFetchFilterOptions,
    searchQuery,
    onSearchInputChange,
    searchTerms,
    addSearchTerm,
    removeSearchTerm,
    logicChain,
    onLogicChainChange,
    onSearch,
    isSearching,
    searchResults,
    error,
    resetSearch,
    activeFilterCount,
    hasActiveFilters,
    filterSummary,
    validation,
  } = useSegments({}, graphQLSegmentsApiService);

  const runUnifiedSearches = useCallback(
    (terms, excl, usedFilters, extraOptions = {}) => {
      if (unifiedAbortRef.current) unifiedAbortRef.current.abort();
      unifiedAbortRef.current = new AbortController();

      setUnifiedLoading(true);
      setUnifiedError(null);

      graphQLSegmentsApiService
        .performUnifiedSetSearch(terms, excl, usedFilters, {
          ...extraOptions,
          signal: unifiedAbortRef.current.signal,
          unifiedSetType,
          sortBy: extraOptions.sortBy || "compositeScore",
          minSegments: minSegments ?? undefined,
          andMultiplier: andMultiplier ?? undefined,
          highestScaleRelevanceWeight,
          bestPerformanceRelevanceWeight,
          oxsWeight,
          llmRelevanceGateEnabled,
          ...(unifiedSetType === "TOP_N"
            ? { topNSegments }
            : {
                reachGoal: reachGoalM * 1_000_000,
                segmentRetentionRate: segmentRetentionRate ?? undefined,
                maxOptimisationRounds: maxOptimisationRounds ?? undefined,
              }),
          highestScaleStartSize: highestScaleStartSize ?? undefined,
          bestPerformanceStartSize: bestPerformanceStartSize ?? undefined,
          mostRelevantStartSize: mostRelevantStartSize ?? undefined,
        })
        .then((r) => {
          if (r.success) setUnifiedResult(r);
          else setUnifiedError(r.error || "Unified set build failed");
        })
        .catch((err) => { if (err.name !== "AbortError") setUnifiedError(err.message); })
        .finally(() => setUnifiedLoading(false));
    },
    [unifiedSetType, topNSegments, reachGoalM, minSegments, andMultiplier, highestScaleRelevanceWeight, bestPerformanceRelevanceWeight, oxsWeight, llmRelevanceGateEnabled, segmentRetentionRate, maxOptimisationRounds, highestScaleStartSize, bestPerformanceStartSize, mostRelevantStartSize],
  );

  const handleSearch = useCallback(() => {
    const includeTerms = searchTerms.filter((t) => t.type === "include" && (t.text || t.query));
    const excludeTerms = searchTerms.filter((t) => t.type === "exclude" && (t.text || t.query));

    const currentQuery = searchQuery?.trim();
    if (currentQuery && !searchTerms.some((t) => t.text === currentQuery)) {
      includeTerms.push({
        text: currentQuery,
        threshold: SEGMENT_DEFAULT_THRESHOLD,
        type: "include",
      });
    }

    const finalTerms = includeTerms.map((t) => ({
      text: t.text || t.query,
      score: resolveQueryThreshold(t, "SEGMENTS"),
      type: "include",
    }));
    const finalExcl = excludeTerms.map((t) => ({
      text: t.text || t.query,
      score: resolveQueryThreshold(t, "SEGMENTS"),
      type: "exclude",
    }));

    if (isSegmentMode) {
      if (segmentSearchAbortRef.current) segmentSearchAbortRef.current.abort();
      segmentSearchAbortRef.current = new AbortController();
      setSegmentSearchLoading(true);
      setSegmentSearchResult(null);
      setSegmentSearchError(null);
      graphQLSegmentsApiService
        .performSegmentSearch(finalTerms, finalExcl, filters, { signal: segmentSearchAbortRef.current.signal })
        .then((r) => {
          if (r.success) { setSegmentSearchResult(r); setActiveResultsTab("segments"); }
          else setSegmentSearchError(r.error || "Segment search failed");
        })
        .catch((err) => { if (err.name !== "AbortError") setSegmentSearchError(err.message); })
        .finally(() => setSegmentSearchLoading(false));
    } else {
      lastSearchContextRef.current = { terms: finalTerms, excl: finalExcl, filters };
      runUnifiedSearches(finalTerms, finalExcl, filters);
    }
  }, [searchTerms, searchQuery, filters, isSegmentMode, runUnifiedSearches]);

  // Re-run with current config when user clicks Re-run button
  const handleUnifiedRefresh = useCallback(() => {
    if (!lastSearchContextRef.current) return;
    const { terms, excl, filters: usedFilters, options: usedOptions = {} } = lastSearchContextRef.current;
    runUnifiedSearches(terms, excl, usedFilters, usedOptions);
  }, [runUnifiedSearches]);

  // Toggles between Unified Set and Segment Search mode.
  // The actual search fires only when the Search button is clicked.
  const handleToggleSegmentMode = useCallback(() => {
    setIsSegmentMode((prev) => !prev);
  }, []);

  // ── NLP / LLM gate ──────────────────────────────────────────────

  // Populate the filter sidebar from the NLP parsed filters
  const applyLlmFiltersToSidebar = useCallback((nlpFilters) => {
    if (!nlpFilters) return;
    const df = nlpFilters.demographicFilters;
    const sm = nlpFilters.segmentMetrics;

    if (df?.ageRange?.start != null && df?.ageRange?.end != null) {
      updateFilter("demographicAge", { start: String(df.ageRange.start), end: String(df.ageRange.end) });
    }
    if (df?.gender) {
      const g = df.gender.trim().toLowerCase();
      const normalized = g === "male" ? "Male" : g === "female" ? "Female" : null;
      if (normalized) updateFilter("demographicGender", normalized);
    }
    if (Array.isArray(df?.location) && df.location.length > 0) {
      updateFilter("demographicLocation", {
        type: (df.locationType || "STATE").toLowerCase(),
        selections: df.location,
      });
    }
    if (sm?.reach > 0) updateFilter("reach", sm.reach);
    if (Array.isArray(nlpFilters.categoryFilters) && nlpFilters.categoryFilters.length > 0) {
      updateFilter("categoryFilters", nlpFilters.categoryFilters);
    }
  }, [updateFilter]);

  const handleNlpSearch = useCallback(async () => {
    if (!nlpText.trim()) return;

    setNlpLoading(true);
    setNlpError(null);
    setNlpInterpretation(null);

    try {
      const response = await graphQLSegmentsApiService.searchSegmentsByText(nlpText.trim(), "UNIFIED_SET");
      if (!response.success) {
        if (response.status === 401 || response.status === 403) { navigate("/"); return; }
        throw new Error(response.error || "NLP search failed");
      }

      // UNIFIED_SET queryType returns unifiedSetParams; fall back to segmentSearchParams for safety
      const unifiedParams = response.data?.parsedQuery?.unifiedSetParams;
      const searchInput = unifiedParams?.segmentSearchInput || response.data?.parsedQuery?.segmentSearchParams;
      if (!searchInput) throw new Error("NLP search returned no parsed parameters");

      const terms = (searchInput.query || []).map((q) => ({ query: q.query, threshold: q.threshold }));
      const excl  = (searchInput.excludeQuery || []).map((q) => ({ query: q.query, threshold: q.threshold }));

      const nlpFilters = searchInput.filters || {};
      const llmFilters = {
        segmentVector: {
          ...(nlpFilters.segmentVector?.priceCpm != null && { priceCpm: nlpFilters.segmentVector.priceCpm }),
        },
        ...(nlpFilters.country?.length && { country: nlpFilters.country }),
        ...(nlpFilters.region != null  && { region: nlpFilters.region }),
        segmentMetrics: {
          reach: nlpFilters.segmentMetrics?.reach,
          avgEcpm: nlpFilters.segmentMetrics?.avgCpm,
          soldImpressions: nlpFilters.segmentMetrics?.soldImpressions,
          weightedClickThroughRate: nlpFilters.segmentMetrics?.weightedClickThroughRate,
          weightedVideoThroughRate: nlpFilters.segmentMetrics?.weightedVideoThroughRate,
          impressionFrequencyPerDay: nlpFilters.segmentMetrics?.impressionFrequencyPerDay,
        },
        demographicFilters: {
          ageRange: nlpFilters.demographicFilters?.ageRange
            ? { start: String(nlpFilters.demographicFilters.ageRange.start), end: String(nlpFilters.demographicFilters.ageRange.end) }
            : { start: "", end: "" },
          location: nlpFilters.demographicFilters?.location || [],
          locationType: nlpFilters.demographicFilters?.locationType?.toLowerCase() || null,
          gender: nlpFilters.demographicFilters?.gender || null,
        },
        categoryFilters: nlpFilters.categoryFilters || [],
      };

      applyLlmFiltersToSidebar(nlpFilters);

      const llmOptions = {
        ...(searchInput.sortBy != null                && { sortBy: searchInput.sortBy }),
        ...(searchInput.sortDirection != null        && { view: searchInput.sortDirection }),
        ...(searchInput.startIndex != null           && { startIndex: searchInput.startIndex }),
        ...(searchInput.limit != null                && { noOfResult: searchInput.limit }),
        ...(searchInput.lookbackWindowInDays != null && { noOfDays: searchInput.lookbackWindowInDays }),
        ...(searchInput.includeAudience != null      && { isAudienceRequire: searchInput.includeAudience }),
      };

      setNlpInterpretation({ terms, excl, filters: nlpFilters, llmParsed: response.data?.llmParsed });
      lastSearchContextRef.current = { terms, excl, filters: llmFilters, options: llmOptions };
      runUnifiedSearches(terms, excl, llmFilters, llmOptions);
    } catch (err) {
      setNlpError(err.message || "NLP search failed");
    } finally {
      setNlpLoading(false);
    }
  }, [nlpText, navigate, applyLlmFiltersToSidebar, runUnifiedSearches]);
  // ────────────────────────────────────────────────────────────────

  // Abort on unmount
  useEffect(() => {
    return () => {
      if (unifiedAbortRef.current) unifiedAbortRef.current.abort();
      if (segmentSearchAbortRef.current) segmentSearchAbortRef.current.abort();
    };
  }, []);

  // Combine segments from the unified set result into the shape SegmentsResultsList expects.
  // Segment-level score/compositeScore are null from the API; enrich each with the best
  // score found across all segmentSets (highest score wins when a segment appears in multiple sets).
  const unifiedSegmentsData = useMemo(() => {
    const sources = [unifiedResult].filter((r) => r?.data);
    if (!sources.length) return {};

    const segmentMap = new Map(); // segmentId → segment object (first source wins for base data)
    const scoreMap = {};          // segmentId → highest score across all sets & sources

    sources.forEach(({ data }) => {
      const { segments = [], segmentSets = [] } = data;

      segments.forEach((seg) => {
        const id = seg.segmentVector?.segmentId;
        if (!id) return;
        if (!segmentMap.has(id)) segmentMap.set(id, seg);
      });

      segmentSets.forEach((set) => {
        (set.segmentScores || []).forEach(({ segmentId, score }) => {
          if (scoreMap[segmentId] == null || score > scoreMap[segmentId]) {
            scoreMap[segmentId] = score;
          }
        });
      });
    });

    if (!segmentMap.size) return {};

    const enriched = Array.from(segmentMap.values())
      .map((seg) => {
        const id = seg.segmentVector?.segmentId;
        const best = id != null ? scoreMap[id] : null;
        return best != null ? { ...seg, score: best, compositeScore: best } : seg;
      })
      .sort((a, b) => (b.compositeScore ?? 0) - (a.compositeScore ?? 0));

    return { Unified: { data: enriched, totalCount: enriched.length, cumulativeData: {} } };
  }, [unifiedResult]);

  // When segment search has run, use those results; otherwise fall back to the unified set's deduped segments.
  // segmentSearchResult.data is { [keyword_key]: { data, totalCount, cumulativeData } } — the same shape
  // SegmentsResultsList already understands, so no transformation needed.
  // isSegmentMode drives the button appearance immediately on click.
  // segmentSearchResult holds the actual data once the call completes.
  const segmentResultsData = (isSegmentMode && segmentSearchResult?.data) ? segmentSearchResult.data : unifiedSegmentsData;
  const segmentResultsCount = (isSegmentMode && segmentSearchResult)
    ? Object.values(segmentSearchResult.data || {}).reduce((sum, g) => sum + (g.totalCount || 0), 0)
    : (unifiedSegmentsData.Unified?.totalCount ?? 0);
  const segmentResultsFlat = (isSegmentMode && segmentSearchResult)
    ? Object.values(segmentSearchResult.data || {}).flatMap((g) => g.data || [])
    : (unifiedSegmentsData.Unified?.data || []);
  const segmentResultsLoading = segmentSearchLoading || (!isSegmentMode && unifiedLoading);

  const tabStyle = (active, color) => ({
    padding: "8px 20px",
    border: "none",
    borderBottom: active ? `3px solid ${color}` : "3px solid transparent",
    background: "none",
    cursor: "pointer",
    fontWeight: active ? 700 : 500,
    fontSize: "0.9rem",
    color: active ? color : "#666",
    transition: "color 0.15s, border-color 0.15s",
  });

  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      {/* Header */}
      <header
        style={{
          background: "linear-gradient(135deg, #0076bd 0%, #1fbab0 100%)",
          color: "white",
          padding: "1rem 2rem",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700 }}>
              Discover Inventory
            </h1>
            <span
              style={{
                background: "rgba(255,255,0,0.25)",
                border: "1px solid rgba(255,255,0,0.5)",
                borderRadius: "4px",
                padding: "2px 8px",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.05em",
              }}
            >
              TEST · GraphQL API
            </span>
          </div>
          <p style={{ margin: "0.2rem 0 0", fontSize: "0.9rem", opacity: 0.85 }}>
            Segment search via <code style={{ opacity: 0.9 }}>/api/graphql</code>
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* Environment selector */}
          <div style={{ display: "flex", alignItems: "center", background: "rgba(0,0,0,0.2)", borderRadius: "8px", padding: "3px", gap: "2px" }}>
            {ENVIRONMENTS.map((env) => {
              const isActive = activeEnv === env.key;
              return (
                <button
                  key={env.key}
                  onClick={() => handleEnvChange(env)}
                  style={{
                    padding: "3px 12px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.78rem",
                    fontWeight: isActive ? 700 : 400,
                    background: isActive ? "rgba(255,255,255,0.95)" : "transparent",
                    color: isActive ? "#0076bd" : "rgba(255,255,255,0.75)",
                    transition: "all 0.15s",
                  }}
                >
                  {env.label}
                </button>
              );
            })}
          </div>

          {/* LLM Gate toggle — always visible in header */}
          <button
            onClick={() => setLlmRelevanceGateEnabled((v) => !v)}
            title="When ON, the backend runs an LLM step after the DB fetch to verify each segment truly matches your search intent."
            style={{
              padding: "0.42rem 1rem",
              background: llmRelevanceGateEnabled ? "rgba(107,79,187,0.85)" : "rgba(255,255,255,0.12)",
              border: llmRelevanceGateEnabled ? "1px solid rgba(200,180,255,0.6)" : "1px solid rgba(255,255,255,0.3)",
              borderRadius: "8px",
              color: "white",
              cursor: "pointer",
              fontSize: "0.85rem",
              fontWeight: llmRelevanceGateEnabled ? 700 : 400,
              letterSpacing: "0.01em",
            }}
          >
            🧠 AI Gate {llmRelevanceGateEnabled ? "ON" : "OFF"}
          </button>

          {/* AI Search toggle */}
          <button
            onClick={() => setNlpMode((v) => !v)}
            style={{
              padding: "0.42rem 1rem",
              background: nlpMode ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.35)",
              borderRadius: "8px",
              color: nlpMode ? "#6b4fbb" : "white",
              cursor: "pointer",
              fontSize: "0.85rem",
              fontWeight: nlpMode ? 700 : 400,
            }}
          >
            ✨ AI Search {nlpMode ? "▲" : "▼"}
          </button>

          <button
            onClick={() => navigate("/")}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "8px",
              padding: "0.5rem 1rem",
              color: "white",
              cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            ← Back to main app
          </button>
        </div>
      </header>

      <div className="domain-search-app" style={{ display: "flex", flex: 1 }}>
        <SegmentsFiltersSidebar
          showFilters={showFilters}
          filters={filters}
          expandedSections={expandedSections}
          onToggleSection={toggleSection}
          onUpdateFilter={updateFilter}
          onToggleArrayFilter={toggleArrayFilter}
          onClearAllFilters={clearAllFilters}
          onClose={() => setShowFilters(false)}
          filterOptions={filterOptions}
          isLoadingFilterOptions={isLoadingFilterOptions}
          hasFilterError={hasFilterError}
          filterError={filterError}
          retryFetchFilterOptions={retryFetchFilterOptions}
          fetchFilterOptions={retryFetchFilterOptions}
          activeSubTab={activeSubTab}
          onSubTabChange={setActiveSubTab}
          validation={validation}
          hasActiveFilters={hasActiveFilters}
          activeFilterCount={activeFilterCount}
          filterSummary={filterSummary}
        />

        <div className={`main-content ${!showFilters ? "full-width" : ""}`}>
          {/* ── NLP input panel (AI mode only) ── */}
          {nlpMode && (
            <div style={{ background: "#fff", padding: "14px 16px", borderBottom: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: "0.78rem", color: "#6b7280", marginBottom: "8px" }}>
                Describe the audience segments you're looking for in plain English
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                <textarea
                  value={nlpText}
                  onChange={(e) => setNlpText(e.target.value)}
                  placeholder="e.g., Male sports enthusiasts aged 25–40 in California, interested in outdoor activities and fitness. Exclude segments related to children's toys."
                  rows={3}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "0.88rem",
                    resize: "vertical",
                    fontFamily: "inherit",
                    lineHeight: 1.5,
                    outline: "none",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleNlpSearch();
                  }}
                />
                <button
                  onClick={handleNlpSearch}
                  disabled={nlpLoading || !nlpText.trim()}
                  style={{
                    padding: "10px 18px",
                    background: nlpLoading || !nlpText.trim() ? "#d1d5db" : "linear-gradient(135deg, #6b4fbb 0%, #0076bd 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: nlpLoading || !nlpText.trim() ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: "0.88rem",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexShrink: 0,
                  }}
                >
                  {nlpLoading ? (
                    <>
                      <span style={{ display: "inline-block", width: "13px", height: "13px", border: "2px solid rgba(255,255,255,0.35)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                      Thinking…
                    </>
                  ) : "✨ Search"}
                </button>
              </div>

              {nlpError && (
                <div style={{ marginTop: "8px", padding: "8px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", fontSize: "0.8rem", color: "#b91c1c" }}>
                  {nlpError}
                </div>
              )}

              {/* AI interpretation banner */}
              {nlpInterpretation && (
                <div style={{ marginTop: "10px", padding: "12px 14px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px" }}>
                  <div style={{ fontSize: "0.77rem", fontWeight: 700, color: "#1d4ed8", marginBottom: "8px" }}>
                    🧠 AI Interpretation
                  </div>
                  {nlpInterpretation.terms.length > 0 && (
                    <div style={{ marginBottom: "5px" }}>
                      <span style={{ fontSize: "0.74rem", fontWeight: 600, color: "#374151", marginRight: "6px" }}>Include:</span>
                      {nlpInterpretation.terms.map((t, i) => (
                        <span key={i} style={{ display: "inline-block", background: "#dbeafe", color: "#1d4ed8", borderRadius: "4px", padding: "1px 7px", fontSize: "0.74rem", marginRight: "4px", marginBottom: "2px" }}>
                          {t.query || t.text}
                        </span>
                      ))}
                    </div>
                  )}
                  {nlpInterpretation.excl.length > 0 && (
                    <div style={{ marginBottom: "5px" }}>
                      <span style={{ fontSize: "0.74rem", fontWeight: 600, color: "#374151", marginRight: "6px" }}>Exclude:</span>
                      {nlpInterpretation.excl.map((t, i) => (
                        <span key={i} style={{ display: "inline-block", background: "#fee2e2", color: "#b91c1c", borderRadius: "4px", padding: "1px 7px", fontSize: "0.74rem", marginRight: "4px", marginBottom: "2px" }}>
                          {t.query || t.text}
                        </span>
                      ))}
                    </div>
                  )}
                  {(() => {
                    const df = nlpInterpretation.filters?.demographicFilters || {};
                    const cats = nlpInterpretation.filters?.categoryFilters || [];
                    const hints = [];
                    if (df.ageRange?.start != null && df.ageRange?.end != null) hints.push(`Age ${df.ageRange.start}–${df.ageRange.end}`);
                    if (df.gender) hints.push(`Gender: ${df.gender}`);
                    if (Array.isArray(df.location) && df.location.length) hints.push(`Location: ${df.location.slice(0, 3).join(", ")}${df.location.length > 3 ? "…" : ""}`);
                    if (cats.length) hints.push(`Categories: ${cats.map((c) => c.categoryL2).slice(0, 3).join(", ")}`);
                    const parsed = nlpInterpretation.llmParsed;
                    return (
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px" }}>
                        {parsed != null && (
                          <span style={{ fontSize: "0.72rem", color: parsed ? "#15803d" : "#b45309", fontWeight: 600, marginRight: "4px" }}>
                            {parsed ? "✓ LLM parsed" : "⚠ Vector fallback"}
                          </span>
                        )}
                        {hints.map((h, i) => (
                          <span key={i} style={{ display: "inline-block", background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", borderRadius: "4px", padding: "1px 7px", fontSize: "0.74rem" }}>
                            {h}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* ── Keyword search interface (keyword mode only) ── */}
          {!nlpMode && (
          <SegmentsSearchInterface
            searchQuery={searchQuery}
            onSearchInputChange={onSearchInputChange}
            searchTerms={searchTerms}
            addSearchTerm={addSearchTerm}
            removeSearchTerm={removeSearchTerm}
            logicChain={logicChain}
            onLogicChainChange={onLogicChainChange}
            onSearch={handleSearch}
            isSearching={unifiedLoading}
            searchResults={null}
            error={null}
            filters={filters}
            updateFilter={updateFilter}
            onRefresh={resetSearch}
            viewMode={viewMode}
            setViewMode={setViewMode}
            isFilterSidebarOpen={showFilters}
            onToggleFilterSidebar={() => setShowFilters(!showFilters)}
            activeSubTab={activeSubTab}
            onSubTabChange={setActiveSubTab}
          />
          )}

          {/* Controls bar: segment search toggle */}
          <div style={{ background: "#fafafa", borderBottom: "1px solid #e8e8e8", padding: "6px 16px", display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
            <button
              onClick={handleToggleSegmentMode}
              style={{
                padding: "4px 14px",
                background: isSegmentMode ? "#0076bd" : "#fff",
                border: `1px solid ${isSegmentMode ? "#0076bd" : "#ccc"}`,
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.8rem",
                fontWeight: 500,
                color: isSegmentMode ? "#fff" : "#555",
              }}
            >
              📋 Segment Search
            </button>
            {isSegmentMode && !segmentSearchLoading && segmentSearchResult && (
              <span style={{ fontSize: "0.75rem", color: "#0076bd" }}>
                ✓ {segmentResultsCount} result{segmentResultsCount !== 1 ? "s" : ""}
                <button
                  onClick={() => { setIsSegmentMode(false); setSegmentSearchResult(null); }}
                  style={{ marginLeft: "6px", background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: "0.75rem", padding: 0 }}
                  title="Revert to Unified Set results"
                >✕</button>
              </span>
            )}
            {segmentSearchError && (
              <span style={{ fontSize: "0.75rem", color: "#dc2626" }}>⚠ {segmentSearchError}</span>
            )}
          </div>

          {/* Results tab bar */}
          <div style={{ background: "#fff", borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", paddingLeft: "16px", gap: "4px" }}>
            <button style={tabStyle(activeResultsTab === "segments", "#0076bd")} onClick={() => setActiveResultsTab("segments")}>
              Segment Results
              {segmentResultsLoading && (
                <span style={{ marginLeft: "6px", display: "inline-block", width: "10px", height: "10px", border: "2px solid #ccc", borderTopColor: "#0076bd", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              )}
              {!segmentResultsLoading && segmentResultsCount > 0 && (
                <span style={{ marginLeft: "6px", background: "#0076bd", color: "#fff", borderRadius: "10px", padding: "1px 7px", fontSize: "0.72rem" }}>
                  {segmentResultsCount}
                </span>
              )}
            </button>
            <button style={tabStyle(activeResultsTab === "unified", "#6b4fbb")} onClick={() => setActiveResultsTab("unified")}>
              Unified Sets
              {unifiedLoading && (
                <span style={{ marginLeft: "6px", display: "inline-block", width: "10px", height: "10px", border: "2px solid #ccc", borderTopColor: "#6b4fbb", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              )}
              {!unifiedLoading && unifiedResult?.success && (
                <span style={{ marginLeft: "6px", background: "#6b4fbb", color: "#fff", borderRadius: "10px", padding: "1px 7px", fontSize: "0.72rem" }}>
                  {unifiedResult.data?.segments?.length ?? 0}
                </span>
              )}
            </button>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>

          {/* Both panels stay mounted; CSS hides the inactive one */}
          <div style={{ display: activeResultsTab === "segments" ? "block" : "none" }}>
            <SegmentsResultsList
              segmentsData={segmentResultsData}
              segments={segmentResultsFlat}
              loading={segmentResultsLoading}
              error={segmentSearchError || null}
              onSegmentSelect={() => {}}
              onSegmentView={() => {}}
              onSegmentAdd={() => {}}
              onSetSelect={() => {}}
              onSetAdd={() => {}}
              selectedSets={[]}
              selectedSegments={[]}
              onSelectAll={() => {}}
              isAllSelected={false}
              isExporting={false}
              onExport={() => {}}
              activeTab="segments"
              currentFilters={filters}
              hasActiveFilters={hasActiveFilters}
              activeFilterCount={activeFilterCount}
              onSearch={onSearch}
              searchTerms={searchTerms}
              logicChain={logicChain}
              multiScoredData={null}
            />
          </div>
          <div style={{ display: activeResultsTab === "unified" ? "block" : "none" }}>
            <UnifiedSetsPanel
              result={unifiedResult}
              loading={unifiedLoading}
              error={unifiedError}
              unifiedSetType={unifiedSetType}
              setUnifiedSetType={setUnifiedSetType}
              topNSegments={topNSegments}
              setTopNSegments={setTopNSegments}
              reachGoalM={reachGoalM}
              setReachGoalM={setReachGoalM}
              onRefresh={handleUnifiedRefresh}
              hasSearchContext={!!lastSearchContextRef.current}
              minSegments={minSegments}
              setMinSegments={setMinSegments}
              andMultiplier={andMultiplier}
              setAndMultiplier={setAndMultiplier}
              highestScaleRelevanceWeight={highestScaleRelevanceWeight}
              setHighestScaleRelevanceWeight={setHighestScaleRelevanceWeight}
              bestPerformanceRelevanceWeight={bestPerformanceRelevanceWeight}
              setBestPerformanceRelevanceWeight={setBestPerformanceRelevanceWeight}
              oxsWeight={oxsWeight}
              setOxsWeight={setOxsWeight}
              llmRelevanceGateEnabled={llmRelevanceGateEnabled}
              setLlmRelevanceGateEnabled={setLlmRelevanceGateEnabled}
              segmentRetentionRate={segmentRetentionRate}
              setSegmentRetentionRate={setSegmentRetentionRate}
              maxOptimisationRounds={maxOptimisationRounds}
              setMaxOptimisationRounds={setMaxOptimisationRounds}
              highestScaleStartSize={highestScaleStartSize}
              setHighestScaleStartSize={setHighestScaleStartSize}
              bestPerformanceStartSize={bestPerformanceStartSize}
              setBestPerformanceStartSize={setBestPerformanceStartSize}
              mostRelevantStartSize={mostRelevantStartSize}
              setMostRelevantStartSize={setMostRelevantStartSize}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraphQLTestPage;
